import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { UserPlus, Gift, DollarSign, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BENEFITS = [
  {
    Icon: Gift,
    title: "First referral",
    body: "Earn a free 1:1 color consult on us — or full reimbursement if you already paid for one — plus a $150 finder's fee when they book.",
  },
  {
    Icon: DollarSign,
    title: "Every referral after",
    body: "A flat $150 finder's fee the moment your friend books a consult or job with Beverly Hills Paint Co.",
  },
  {
    Icon: CheckCircle2,
    title: "No cap on referrals",
    body: "Refer as many friends, neighbors and business owners as you'd like. The math is simple — you earn on every single booking.",
  },
];

export function Refer() {
  const empty = {
    referrer_name: "",
    referrer_email: "",
    referrer_phone: "",
    friend_name: "",
    friend_email: "",
    friend_phone: "",
    friend_city: "",
    project_notes: "",
    consent: true,
  };
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.referrer_name || !form.referrer_email || !form.friend_name) {
      toast.error("Please share your name, email and your friend's name so we can follow up.");
      return;
    }
    if (!form.friend_email && !form.friend_phone) {
      toast.error("Please add an email or phone for your friend so we can reach them.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.friend_email) delete payload.friend_email;
      const r = await axios.post(`${API}/referrals`, payload);
      setSent(r.data);
      setForm(empty);
      toast.success("Thank you! We'll reach out to your friend within one business day.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="refer-page" className="pt-32 bg-ivory">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-6 pb-16 grid md:grid-cols-[1.15fr_1fr] gap-14 items-center">
        <motion.div initial={{opacity:0, y:20}} animate={{opacity:1, y:0}} transition={{duration:0.8, ease:[0.22,1,0.36,1]}}>
          <p className="overline text-gold" data-testid="refer-eyebrow">Refer &amp; Earn</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-4">Know someone who also needs painting?</h1>
          <p className="text-ink/75 mt-6 leading-relaxed text-lg max-w-xl">
            A lot of our best clients hear from friends, neighbors and business owners who fall in love with the finished result.
            Send them our way and we'll thank you every time they book.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#refer-form" data-testid="refer-hero-cta" className="inline-flex items-center gap-2 bg-ink text-white overline px-8 py-4 hover:bg-gold transition-colors"><UserPlus size={15}/> Refer someone now</a>
            <Link to="/portal/referrals" data-testid="refer-portal-link" className="inline-flex items-center gap-2 border border-ink text-ink overline px-8 py-4 hover:bg-ink hover:text-white transition-colors">Track my referrals</Link>
          </div>
        </motion.div>
        <motion.div initial={{opacity:0, scale:0.96}} animate={{opacity:1, scale:1}} transition={{duration:0.9, ease:[0.22,1,0.36,1], delay:0.15}} className="bg-white border border-black/10 p-8">
          <p className="eyebrow text-gold">The reward</p>
          <p className="font-serif text-3xl mt-3 leading-tight">Free 1:1 color consult + $150 for every friend that books.</p>
          <div className="mt-6 space-y-4">
            {BENEFITS.map(({ Icon, title, body }) => (
              <div key={title} className="flex gap-3">
                <div className="mt-1 shrink-0 h-8 w-8 rounded-full bg-gold/15 text-gold flex items-center justify-center"><Icon size={15} /></div>
                <div>
                  <p className="font-serif text-lg">{title}</p>
                  <p className="text-sm text-ink/70 mt-1 leading-relaxed">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* FORM */}
      <section id="refer-form" className="bg-white border-y border-black/10 py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {sent ? (
            <div className="text-center py-8" data-testid="refer-success">
              <div className="w-16 h-16 mx-auto rounded-full bg-gold text-white flex items-center justify-center"><CheckCircle2 size={30}/></div>
              <h2 className="font-serif text-4xl md:text-5xl mt-6">Thank you.</h2>
              <p className="text-ink/70 mt-4 max-w-xl mx-auto leading-relaxed">
                We'll reach out to your friend within one business day. The moment they book a consult or job with us, your reward is triggered.
                {sent.referral_index === 1
                  ? " Since this is your first referral, you've earned a free 1:1 color consult (or reimbursement) plus a $150 finder's fee."
                  : " You've earned another $150 finder's fee once they book."}
              </p>
              <div className="mt-8 flex flex-wrap gap-3 justify-center">
                <button onClick={() => setSent(null)} data-testid="refer-again-btn" className="inline-flex items-center gap-2 bg-ink text-white overline px-8 py-4 hover:bg-gold transition-colors"><UserPlus size={15}/> Refer another friend</button>
                <Link to="/portal/referrals" data-testid="refer-view-portal" className="inline-flex items-center gap-2 border border-ink text-ink overline px-8 py-4 hover:bg-ink hover:text-white transition-colors">See my referrals</Link>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-8" data-testid="refer-form">
              <div>
                <p className="eyebrow text-gold">Your details</p>
                <h2 className="font-serif text-3xl md:text-4xl mt-2">Tell us who's sending them.</h2>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <Field label="Your full name *" value={form.referrer_name} onChange={set("referrer_name")} testid="refer-you-name" required />
                <Field label="Your email *" type="email" value={form.referrer_email} onChange={set("referrer_email")} testid="refer-you-email" required />
                <Field label="Your phone" value={form.referrer_phone} onChange={set("referrer_phone")} testid="refer-you-phone" />
              </div>
              <div>
                <p className="eyebrow text-gold">Your friend</p>
                <h2 className="font-serif text-3xl md:text-4xl mt-2">Who should we reach out to?</h2>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Friend's name *" value={form.friend_name} onChange={set("friend_name")} testid="refer-friend-name" required />
                <Field label="Friend's city" value={form.friend_city} onChange={set("friend_city")} testid="refer-friend-city" />
                <Field label="Friend's email" type="email" value={form.friend_email} onChange={set("friend_email")} testid="refer-friend-email" />
                <Field label="Friend's phone" value={form.friend_phone} onChange={set("friend_phone")} testid="refer-friend-phone" />
              </div>
              <label className="block">
                <p className="overline text-[10px] text-ink/60 mb-1">A little about their project</p>
                <textarea rows={4} value={form.project_notes} onChange={set("project_notes")} data-testid="refer-notes" className="w-full border border-black/15 focus:border-gold px-4 py-3 text-sm bg-ivory outline-none" placeholder="e.g. Whole-home interior repaint in Bel Air, likely late summer." />
              </label>
              <label className="flex items-start gap-2 text-xs text-ink/70">
                <input type="checkbox" checked={form.consent} onChange={(e)=>setForm({...form, consent: e.target.checked})} data-testid="refer-consent" className="mt-1" />
                <span>I have their permission to share their contact info with Beverly Hills Paint Co.</span>
              </label>
              <Button type="submit" disabled={loading} data-testid="refer-submit" className="w-full rounded-none bg-gold text-white hover:bg-gold-dark py-6 overline">
                {loading ? "Sending referral…" : "Send referral"}
              </Button>
              <p className="text-[11px] text-ink/50 text-center">Referrals are tracked automatically when you're signed in. <Link to="/signup" className="text-gold underline">Create a free account</Link> to see your rewards in your portal.</p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text", required }) {
  return (
    <label className="block">
      <p className="overline text-[10px] text-ink/60 mb-1">{label}</p>
      <input required={required} type={type} value={value} onChange={onChange} data-testid={testid} className="w-full border border-black/15 focus:border-gold px-4 py-3 text-sm bg-ivory outline-none" />
    </label>
  );
}
